import axiosInstance from "../api/axiosInstance";
import {
  Course,
  CourseCreateDto,
  CourseUpdateDto,
  PaginatedCoursesResponse,
  CourseFilterParams,
  CourseCfn,
  CourseCfnCreateDto,
  CourseCfnUpdateDto,
  PaginatedCourseCfnsResponse,
  CatalogSearchParams,
  PaginatedCatalogResponse,
  CourseCategory,
  PaginatedCourseCategoriesResponse,
  CourseRatingCreate,
  PaginatedCourseRatingsResponse,
  CourseDetailsForUser,
  CatalogItem,
} from "../types/course.types";
import { buildQueryString } from "../utils/query";
import { ExportFormat } from "../constants/export-dropdown.constants";
import { ApiCertificateIssueResponse } from "@/components/admin/Courses/CourseBuilder/types/courseContentApi.types";
import { AxiosError } from "axios";

import { CATALOG_ENDPOINTS, CATEGORY_ENDPOINTS, COURSE_ENDPOINTS } from "@/api/endpoints/course.endpoints";
import { ManagementApiService, FormApiService } from "@/types/management.types";

export const CourseManagementApiService: ManagementApiService<
  Course,
  CourseFilterParams
> = {
  getAll: async (
    offset = 0,
    limit = 10,
    filters?: CourseFilterParams
  ): Promise<PaginatedCoursesResponse> => {
    const params: Record<string, any> = {
      offset,
      limit,
    };

    if (filters?.fullname)    params.fullname    = filters.fullname;
    if (filters?.shortname)   params.shortname   = filters.shortname;
    if (filters?.category_id) params.category_id = filters.category_id;
    if (filters?.language)    params.language    = filters.language;
    if (filters?.category_ids) params.category_ids = filters.category_ids;

    const response = await axiosInstance.get<PaginatedCoursesResponse>(
      COURSE_ENDPOINTS.COURSES,
      { params },
    );

    return response.data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(COURSE_ENDPOINTS.COURSE_DETAIL(id));
  },
  exportItems: async (
    format: ExportFormat,
    filters?: Omit<CourseFilterParams, "offset" | "limit">
  ): Promise<Blob> => {
    const queryParams = { ...filters, format: format.toLowerCase() };
    const query = buildQueryString(queryParams);
    const response = await axiosInstance.get<Blob>(
      `${COURSE_ENDPOINTS.COURSES_EXPORT}${query}`,
      { responseType: "blob" }
    );
    return response.data;
  },
};

export const CourseFormApiService: FormApiService<
  Course,
  CourseCreateDto,
  CourseUpdateDto
> = {
  createItem: async (data: CourseCreateDto): Promise<Course> => {
    return (await axiosInstance.post<Course>(COURSE_ENDPOINTS.COURSES, data)).data;
  },
  updateItem: async (id: number, data: CourseUpdateDto): Promise<Course> => {
    return (await axiosInstance.put<Course>(COURSE_ENDPOINTS.COURSE_DETAIL(id), data)).data;
  },
};

const flattenCourseCategoriesInternal = (
  categories: CourseCategory[] | undefined | null,
  depth = 0
): { id: number; name: string; depth: number; isRoot: boolean }[] => {
  let flatList: { id: number; name: string; depth: number; isRoot: boolean }[] = [];
  if (!Array.isArray(categories)) return flatList;

  categories.forEach((category) => {
    flatList.push({
      id: category.id,
      name: `${"— ".repeat(depth)}${category.name.replace(/^—\s*/, "")}`,
      depth,
      isRoot: !!category.is_tenant_root,
    });
    if (category.children && category.children.length > 0) {
      flatList = flatList.concat(
        flattenCourseCategoriesInternal(category.children, depth + 1)
      );
    }
  });
  return flatList;
};

export const CourseApi = {
  /**
   * @param raw when true, keep the `{en}…{en}{ar}…{ar}` multilang tags in
   *   translatable fields (fullname, summary, …). Admin EDIT forms need this
   *   so the per-language tab editors round-trip every translation; display
   *   callers omit it (the global interceptor strips to the active locale).
   */
  getCourseById: async (id: number | string, raw = false): Promise<Course> => {
    const cfg = raw ? ({ skipMultilangStrip: true } as object) : undefined;
    // Try the direct detail endpoint first (full data)
    try {
      const response = await axiosInstance.get<Course>(
        COURSE_ENDPOINTS.COURSE_DETAIL(id),
        cfg,
      );
      return response.data;
    } catch {
      // Fallback: fetch from the list and find by ID
      const listResponse = await axiosInstance.get<PaginatedCoursesResponse>(
        COURSE_ENDPOINTS.COURSES,
        { params: { limit: 200 }, ...(cfg ?? {}) },
      );
      const found = listResponse.data.items?.find(c => c.id === Number(id));
      if (!found) throw new Error(`Course with id ${id} not found`);
      return found;
    }
  },

  getAllCategoriesFlat: async (): Promise<{ id: number; name: string; depth: number; isRoot: boolean }[]> => {
    const endpoint = CATEGORY_ENDPOINTS.CATEGORIES_TREE || CATEGORY_ENDPOINTS.CATEGORIES;
    const response = await axiosInstance.get<PaginatedCourseCategoriesResponse | CourseCategory[]>(endpoint);
    let treeData: CourseCategory[];
    if ("items" in response.data && "total" in response.data) {
      treeData = Array.isArray(response.data.items) ? response.data.items : [];
    } else if (Array.isArray(response.data)) {
      treeData = response.data;
    } else {
      treeData = [];
    }
    return flattenCourseCategoriesInternal(treeData);
  },

  getCourseCustomFieldDefinitions: async (offset = 0, limit = 100): Promise<PaginatedCourseCfnsResponse> => {
    const query = buildQueryString({ offset, limit });
    return (await axiosInstance.get<PaginatedCourseCfnsResponse>(`${COURSE_ENDPOINTS.COURSE_CUSTOM_FIELD_DEFINITIONS}${query}`)).data;
  },

  rateCourse: async (courseId: number | string, data: CourseRatingCreate): Promise<Course> => {
    return (await axiosInstance.post<Course>(COURSE_ENDPOINTS.RATE_COURSE(courseId), data)).data;
  },

  getCourseRatings: async (courseId: number | string, offset = 0, limit = 20): Promise<PaginatedCourseRatingsResponse> => {
    const query = buildQueryString({ offset, limit });
    return (await axiosInstance.get<PaginatedCourseRatingsResponse>(`${COURSE_ENDPOINTS.GET_RATINGS(courseId)}${query}`)).data;
  },

  deleteCourseRating: async (courseId: number | string, ratingId: number | string): Promise<void> => {
    await axiosInstance.delete(COURSE_ENDPOINTS.DELETE_RATING(courseId, ratingId));
  },

  getCourseDetailsForUser: async (courseId: number | string): Promise<CourseDetailsForUser> => {
    return (await axiosInstance.get<CourseDetailsForUser>(
        COURSE_ENDPOINTS.COURSE_DETAILS_FOR_USER(courseId), 
        { params: { limit: 10 } } // Явно передаем маленький лимит
    )).data;
},

  getRelatedCourses: async (courseId: number | string): Promise<CatalogItem[]> => {
    const response = await axiosInstance.get<CatalogItem[]>(
      COURSE_ENDPOINTS.SIMILAR_COURSES(courseId)
    );
    
    return response.data
      .filter(item => item.id !== Number(courseId))
      .slice(0, 4);
  },
};

export const CourseCertificateApiService = {
  checkCompletion: async (courseId: number | string): Promise<ApiCertificateIssueResponse | null> => {
    try {
      const response = await axiosInstance.post<ApiCertificateIssueResponse>(COURSE_ENDPOINTS.CHECK_COMPLETION(courseId));
      if (response.status === 201) {
        return response.data;
      }
      return null;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response && (axiosError.response.status === 204 || axiosError.response.status === 409)) {
        return null;
      }
      throw error;
    }
  },
  getMyCertificateForCourse: async (courseId: number | string): Promise<ApiCertificateIssueResponse | null> => {
    try {
      const response = await axiosInstance.get<ApiCertificateIssueResponse>(COURSE_ENDPOINTS.MY_CERTIFICATE(courseId));
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response && axiosError.response.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

export const CourseCfnManagementApiService: ManagementApiService<CourseCfn, { name?: string }> = {
  getAll: async (offset = 0, limit = 10, filters?: { name?: string }): Promise<PaginatedCourseCfnsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedCourseCfnsResponse>(`${COURSE_ENDPOINTS.COURSE_CUSTOM_FIELD_DEFINITIONS}${query}`)).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(COURSE_ENDPOINTS.COURSE_CUSTOM_FIELD_DEFINITION_DETAIL(id));
  },
};

export const CourseCfnFormApiService: FormApiService<CourseCfn, CourseCfnCreateDto, CourseCfnUpdateDto> = {
  createItem: async (data: CourseCfnCreateDto): Promise<CourseCfn> => {
    let parsedConfig: object | string | null = null;
    if (data.config && typeof data.config === "string") {
      try {
        parsedConfig = JSON.parse(data.config);
      } catch {
        parsedConfig = data.config === "{}" ? {} : null;
      }
    } else if (typeof data.config === "object") {
      parsedConfig = data.config;
    }
    const payload = { ...data, config: parsedConfig };
    return (await axiosInstance.post<CourseCfn>(COURSE_ENDPOINTS.COURSE_CUSTOM_FIELD_DEFINITIONS, payload)).data;
  },
  updateItem: async (id: number, data: CourseCfnUpdateDto): Promise<CourseCfn> => {
    let parsedConfig: object | string | null = null;
    if (data.config && typeof data.config === "string") {
      try {
        parsedConfig = JSON.parse(data.config);
      } catch {
        parsedConfig = data.config === "{}" ? {} : null;
      }
    } else if (typeof data.config === "object") {
      parsedConfig = data.config;
    }
    const payload = { ...data, config: parsedConfig };
    return (await axiosInstance.put<CourseCfn>(COURSE_ENDPOINTS.COURSE_CUSTOM_FIELD_DEFINITION_DETAIL(id), payload)).data;
  },
};

export const CatalogApiService = {
  /** Visibility-respecting totals in ONE request (backend /catalog/counts). */
  counts: async (): Promise<{ courses: number; programs: number; certifications: number }> =>
    (await axiosInstance.get(CATALOG_ENDPOINTS.COUNTS)).data,
  search: async (params: CatalogSearchParams): Promise<PaginatedCatalogResponse> => {
    const response = await axiosInstance.get<PaginatedCatalogResponse>(
      CATALOG_ENDPOINTS.SEARCH,
      {
        params: params,
        paramsSerializer: {
          indexes: null
        }
      }
    );
    return response.data;
  }
};